import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { formatDate } from '../../lib/formatUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { TruncatedCell } from '../../components/common/TruncatedCell';
import { Checkbox } from '../../components/ui/checkbox';
import { CalendarDays, Plus, Loader2, TrendingUp, TrendingDown, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../../components/ConfirmationProvider';

const statusColors = {
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  approved: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
  cancelled: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20',
};

const leaveReasons = [
  { value: '', label: 'Select reason (optional)' },
  { value: 'personal', label: 'Personal' },
  { value: 'medical', label: 'Medical' },
  { value: 'family', label: 'Family Emergency' },
  { value: 'travel', label: 'Travel' },
  { value: 'other', label: 'Other' },
];

export const MyLeavePage = () => {
  const confirm = useConfirmation();
  const [leaves, setLeaves] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({
    start_date: '', end_date: '', reason: '', leave_reason: '',
    is_half_day: false, half_day_period: 'first_half',
  });

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await api.get(`/leave-mgmt/my-requests${params}`);
      if (res.data.success) setLeaves(res.data.data);
    } catch { setLeaves([]); }
    finally { setLoading(false); }
  }, [statusFilter]);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await api.get('/leave-mgmt/my-balance');
      if (res.data.success) setBalance(res.data.data);
    } catch {}
  }, []);

  useEffect(() => { fetchLeaves(); }, [fetchLeaves]);
  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  const isSingleDay = form.start_date && form.end_date && form.start_date === form.end_date;

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) { toast.error('Please select start and end dates'); return; }
    setSubmitting(true);
    try {
      const payload = {
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason,
        leave_reason: form.leave_reason,
        is_half_day: form.is_half_day && isSingleDay,
        half_day_period: form.half_day_period,
      };
      const res = await api.post('/my/leave', payload);
      if (res.data.success) {
        toast.success(res.data.message);
        setDialogOpen(false);
        setForm({ start_date: '', end_date: '', reason: '', leave_reason: '', is_half_day: false, half_day_period: 'first_half' });
        fetchLeaves();
        fetchBalance();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply leave'); }
    finally { setSubmitting(false); }
  };

  const handleCancel = async (leave) => {
    const ok = await confirm({
      title: 'Cancel Leave',
      description: 'Are you sure you want to cancel this leave request?',
      confirmText: 'Cancel Leave',
    });
    if (!ok) return;
    try {
      await api.post(`/leave-mgmt/${leave.id}/cancel`);
      toast.success('Leave cancelled');
      fetchLeaves();
      fetchBalance();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot cancel'); }
  };

  const currentBal = balance?.current_balance ?? 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-leave-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">My Leaves</h1>
          <p className="text-muted-foreground">Apply for earned leave and track your balance</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="apply-leave-btn">
          <Plus className="h-4 w-4 mr-2" />Apply Leave
        </Button>
      </div>

      {/* Balance Cards */}
      {balance && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${currentBal >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                  {currentBal >= 0 ? <TrendingUp className="h-5 w-5 text-emerald-500" /> : <TrendingDown className="h-5 w-5 text-red-500" />}
                </div>
                <div>
                  <p className={`text-2xl font-bold ${currentBal < 0 ? 'text-red-400' : ''}`} data-testid="my-balance">{currentBal}</p>
                  <p className="text-xs text-muted-foreground">Current Balance</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold">{balance.total_accrued}</p><p className="text-xs text-muted-foreground">Total Accrued</p></div></div></CardContent></Card>
          <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-amber-500" /></div><div><p className="text-2xl font-bold">{balance.total_used}</p><p className="text-xs text-muted-foreground">Total Used</p></div></div></CardContent></Card>
          <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center"><CalendarDays className="h-5 w-5 text-purple-500" /></div><div><p className="text-2xl font-bold">{balance.carry_forward_balance}</p><p className="text-xs text-muted-foreground">Carry Forward</p></div></div></CardContent></Card>
        </div>
      )}
      {currentBal < 0 && (
        <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-sm text-red-400 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Your leave balance is negative ({currentBal} days). Further leaves may be marked as Loss of Pay (LOP).
          Minimum allowed: {balance?.negative_balance_limit} days.
        </div>
      )}
      {balance?.is_probation && (
        <div className="p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/20 text-sm text-yellow-500 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          You are in probation period. Leave requests require manager approval.
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="my-leave-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leave History */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : leaves.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-16 text-muted-foreground">
          <CalendarDays className="h-12 w-12 mb-4 opacity-40" /><p>No leave records found</p>
          <Button className="mt-4" onClick={() => setDialogOpen(true)} data-testid="apply-leave-empty"><Plus className="h-4 w-4 mr-2" />Apply Leave</Button>
        </CardContent></Card>
      ) : (
        <Card>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Dates</TableHead>
              <TableHead>Days</TableHead>
              <TableHead>LOP</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Applied</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {leaves.map(leave => (
                <TableRow key={leave.id} data-testid={`my-leave-${leave.id}`}>
                  <TableCell className="text-sm">
                    {formatDate(leave.start_date)}{leave.start_date !== leave.end_date && ` - ${formatDate(leave.end_date)}`}
                    {leave.is_half_day && <Badge variant="outline" className="ml-1 text-xs">Half</Badge>}
                  </TableCell>
                  <TableCell>{leave.days}</TableCell>
                  <TableCell>{(leave.lop_days || 0) > 0 ? <span className="text-red-400">{leave.lop_days}</span> : '-'}</TableCell>
                  <TableCell className="max-w-[260px] text-sm">
                    <TruncatedCell
                      text={leave.reason}
                      maxWidth={250}
                      testId={`my-leave-reason-${leave.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    {(leave.status === 'rejected' && leave.rejection_reason) ? (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center cursor-help" data-testid={`my-leave-rejected-badge-${leave.id}`}>
                              <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs bg-background text-foreground border border-border shadow-lg p-3 space-y-1.5 text-xs">
                            <div className="font-semibold text-red-400 flex items-center gap-1.5">
                              <XCircle className="h-3 w-3" />Rejection reason
                            </div>
                            <div className="leading-snug whitespace-pre-wrap break-words">{leave.rejection_reason}</div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (leave.status === 'approved' && leave.approver_comment) ? (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center cursor-help" data-testid={`my-leave-approved-badge-${leave.id}`}>
                              <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs bg-background text-foreground border border-border shadow-lg p-3 space-y-1.5 text-xs">
                            <div className="font-semibold text-emerald-400">Approver comment</div>
                            <div className="leading-snug whitespace-pre-wrap break-words">{leave.approver_comment}</div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (leave.status === 'cancelled' && leave.admin_cancel_reason) ? (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center cursor-help" data-testid={`my-leave-cancelled-badge-${leave.id}`}>
                              <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs bg-background text-foreground border border-border shadow-lg p-3 space-y-1.5 text-xs">
                            <div className="font-semibold text-amber-400">Cancelled by HR</div>
                            <div className="leading-snug whitespace-pre-wrap break-words">{leave.admin_cancel_reason}</div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(leave.applied_on || leave.created_at)}</TableCell>
                  <TableCell className="text-right">
                    {(leave.status === 'pending' || leave.status === 'approved') && new Date(leave.start_date) > new Date() && (
                      <Button size="sm" variant="ghost" className="text-red-500 h-8" onClick={() => handleCancel(leave)} data-testid={`cancel-leave-${leave.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1" />Cancel
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Apply Leave Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" />Apply for Leave (EL)</DialogTitle>
            <DialogDescription>
              Current balance: <span className={`font-semibold ${currentBal < 0 ? 'text-red-400' : 'text-emerald-500'}`}>{currentBal} days</span>
              {currentBal < 0 && <span className="text-red-400 ml-1">(negative)</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} data-testid="leave-start" />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} data-testid="leave-end" />
              </div>
            </div>
            {isSingleDay && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox checked={form.is_half_day} onCheckedChange={c => setForm(f => ({ ...f, is_half_day: c }))} data-testid="leave-half-day" />
                  <Label>Half Day</Label>
                </div>
                {form.is_half_day && (
                  <Select value={form.half_day_period} onValueChange={v => setForm(f => ({ ...f, half_day_period: v }))}>
                    <SelectTrigger className="w-[160px]" data-testid="leave-half-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="first_half">First Half</SelectItem>
                      <SelectItem value="second_half">Second Half</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason Tag</Label>
              <Select value={form.leave_reason} onValueChange={v => setForm(f => ({ ...f, leave_reason: v }))}>
                <SelectTrigger data-testid="leave-reason-tag"><SelectValue placeholder="Select reason (optional)" /></SelectTrigger>
                <SelectContent>
                  {leaveReasons.map(r => <SelectItem key={r.value || '__empty'} value={r.value || '__empty'}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Details / Notes</Label>
              <Textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Brief description..." rows={3} data-testid="leave-reason-text" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="submit-leave">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarDays className="h-4 w-4 mr-2" />}Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
